import { faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons/faArrowUpRightFromSquare";
import { faDownload } from "@fortawesome/free-solid-svg-icons/faDownload";
import { faEdit } from "@fortawesome/free-solid-svg-icons/faEdit";
import { faFile } from "@fortawesome/free-solid-svg-icons/faFile";
import { faFolder } from "@fortawesome/free-solid-svg-icons/faFolder";
import { faFolderPlus } from "@fortawesome/free-solid-svg-icons/faFolderPlus";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { faGoogleDrive } from "@fortawesome/free-brands-svg-icons/faGoogleDrive";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Tooltip } from "../../utils/local-antd";

import { AtiraEmpty } from "../../components/AtiraEmpty";
import { AtiraIcon } from "../../components/AtiraIcon";
import { AtiraUpload } from "../../components/AtiraUpload";
import { Button } from "../../components/Button";
import { Clickable } from "../../components/Clickable";
import { Flex } from "../../components/Flex";
import { Input } from "../../components/Input";
import { SubHeader } from "../../components/SubHeader";
import { Text } from "../../components/Text";
import { WarningModal } from "../../components/WarningModal";
import { Modal } from "../../components/modal/Modal";
import { AtiraTable } from "../../components/table/AtiraTable";
import { TableHeader } from "../../components/table/TableHeader";
import { GoogleDriveFileDto } from "../../model/integration/dto/google-drive/GoogleDriveFileDto";
import { integrationSliceSelectors } from "../../redux/integration/integration.selector";
import { integrationActions } from "../../redux/integration/integration.slice";
import { formatBytes } from "../../utils/String";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { userSliceSelectors } from "../../redux/user/user.selector";
import { Spacing } from "../../theme/Spacing";
import { AtiraToast } from "../../utils/AtiraToast";
import type { UploadFile } from "../../utils/local-antd";

type BreadcrumbEntry = { id: string; name: string };

const StyledUpload = styled(AtiraUpload)`
  width: fit-content;

  .ant-btn-variant-outlined {
    height: auto;
    padding: ${Spacing.s} ${Spacing.l};
  }
`;

const Breadcrumbs = styled(Flex)`
  align-items: center;
  gap: ${Spacing.xs};
  padding: 0 ${Spacing.m};
  flex-wrap: wrap;
`;

const GoogleDriveRoute: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const isOrgMember = useAppSelector(userSliceSelectors.selectIsUserOrgMember);
  const integration = useAppSelector(
    integrationSliceSelectors.selectGoogleDriveIntegration,
  );
  const files = useAppSelector(
    integrationSliceSelectors.selectGoogleDriveFiles,
  );
  const filesLoading = useAppSelector(
    integrationSliceSelectors.selectGoogleDriveFilesLoading,
  );
  const actionLoading = useAppSelector(
    integrationSliceSelectors.selectGoogleDriveFileActionLoading,
  );

  const [path, setPath] = useState<BreadcrumbEntry[]>([]);
  const [search, setSearch] = useState("");
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [renameTarget, setRenameTarget] = useState<GoogleDriveFileDto | null>(
    null,
  );
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<GoogleDriveFileDto | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const currentFolderId = path[path.length - 1]?.id;

  useEffect(() => {
    dispatch(integrationActions.getUserIntegrations({ userId }));
  }, [dispatch, userId]);

  useEffect(() => {
    if (!integration || integration.config.needsReconnect) {
      return;
    }

    dispatch(
      integrationActions.listGoogleDriveFiles({
        userId,
        integrationId: integration._id,
        folderId: currentFolderId,
        search: search || undefined,
      }),
    );
    // Re-fetches on folder navigation and search; not on every keystroke of
    // `integration`/`userId` identity churn, which selectors already stabilize.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integration?._id, integration?.config.needsReconnect, currentFolderId, search]);

  if (!integration) {
    return (
      <Flex flexDirection="column">
        <SubHeader
          title={t("integration.google-drive.title")}
          icon={faGoogleDrive}
        />
        <AtiraEmpty
          description={t("integration.google-drive.notConnected")}
          icon={faGoogleDrive}
          action={
            isOrgMember
              ? undefined
              : {
                  title: t("integration.google-drive.goToIntegrations"),
                  onClick: () => navigate("/integration"),
                }
          }
        />
      </Flex>
    );
  }

  if (integration.config.needsReconnect) {
    return (
      <Flex flexDirection="column">
        <SubHeader
          title={t("integration.google-drive.title")}
          icon={faGoogleDrive}
        />
        <AtiraEmpty
          description={t("integration.google-drive.needsReconnect")}
          icon={faGoogleDrive}
          action={
            isOrgMember
              ? undefined
              : {
                  title: t("integration.google-drive.goToIntegrations"),
                  onClick: () => navigate("/integration"),
                }
          }
        />
      </Flex>
    );
  }

  const onOpenFolder = (file: GoogleDriveFileDto) => {
    setSearch("");
    setPath([...path, { id: file.id, name: file.name }]);
  };

  const onBreadcrumbClick = (index: number) => {
    setSearch("");
    setPath(path.slice(0, index));
  };

  const onCreateFolder = async () => {
    if (!newFolderName.trim()) {
      return;
    }

    try {
      await dispatch(
        integrationActions.createGoogleDriveFile({
          userId,
          integrationId: integration._id,
          name: newFolderName.trim(),
          parentId:
            currentFolderId || integration.config.grantedRoots?.[0]?.fileId,
        }),
      ).unwrap();

      setCreateFolderOpen(false);
      setNewFolderName("");
    } catch (error) {
      AtiraToast.apiError(error);
    }
  };

  const onUpload = async (file: UploadFile) => {
    const parentId =
      currentFolderId || integration.config.grantedRoots?.[0]?.fileId;

    if (!parentId) {
      AtiraToast.error(t("integration.google-drive.noFolderSelected"));
      return;
    }

    try {
      await dispatch(
        integrationActions.createGoogleDriveFile({
          userId,
          integrationId: integration._id,
          name: file.name,
          parentId,
          file: file.originFileObj as File,
        }),
      ).unwrap();
    } catch (error) {
      AtiraToast.apiError(error);
    }
  };

  const onRename = async () => {
    if (!renameTarget || !renameValue.trim()) {
      return;
    }

    try {
      await dispatch(
        integrationActions.updateGoogleDriveFile({
          userId,
          integrationId: integration._id,
          fileId: renameTarget.id,
          name: renameValue.trim(),
        }),
      ).unwrap();

      setRenameTarget(null);
    } catch (error) {
      AtiraToast.apiError(error);
    }
  };

  const onDelete = async () => {
    if (!deleteTarget) {
      return;
    }

    try {
      setDeleting(true);

      await dispatch(
        integrationActions.deleteGoogleDriveFile({
          userId,
          integrationId: integration._id,
          fileId: deleteTarget.id,
        }),
      ).unwrap();

      setDeleteTarget(null);
    } catch (error) {
      AtiraToast.apiError(error);
    } finally {
      setDeleting(false);
    }
  };

  const onDownload = async (file: GoogleDriveFileDto) => {
    try {
      setDownloadingId(file.id);

      const blob = await dispatch(
        integrationActions.downloadGoogleDriveFile({
          userId,
          integrationId: integration._id,
          fileId: file.id,
        }),
      ).unwrap();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      AtiraToast.apiError(error);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Flex flexDirection="column">
      <SubHeader
        title={t("integration.google-drive.title")}
        icon={faGoogleDrive}
        extra={
          <Flex gap="s">
            <Button
              icon={faFolderPlus}
              onClick={() => setCreateFolderOpen(true)}
              hover
            >
              {t("integration.google-drive.newFolder")}
            </Button>

            <StyledUpload
              onChange={onUpload}
              title={t("integration.google-drive.uploadFile")}
            />
          </Flex>
        }
      />

      <Breadcrumbs>
        <Clickable onClick={() => onBreadcrumbClick(0)}>
          <Text color={path.length ? "main" : "textColor"}>
            {t("integration.google-drive.title")}
          </Text>
        </Clickable>

        {path.map((entry, index) => (
          <Flex key={entry.id} gap="xs" alignItems="center">
            <Text>/</Text>
            <Clickable onClick={() => onBreadcrumbClick(index + 1)}>
              <Text color={index === path.length - 1 ? "textColor" : "main"}>
                {entry.name}
              </Text>
            </Clickable>
          </Flex>
        ))}
      </Breadcrumbs>

      <TableHeader
        onFetch={() =>
          dispatch(
            integrationActions.listGoogleDriveFiles({
              userId,
              integrationId: integration._id,
              folderId: currentFolderId,
              search: search || undefined,
            }),
          )
        }
        onFetchLoading={filesLoading}
        onSearch={(e) => setSearch(e.target.value)}
        showFilters
      />

      <AtiraTable
        data={files}
        loading={filesLoading || actionLoading}
        columns={[
          {
            title: t("common.name"),
            dataIndex: "name",
            key: "name",
            render: (name: string, file: GoogleDriveFileDto) => (
              <Clickable
                onClick={() => (file.isFolder ? onOpenFolder(file) : undefined)}
              >
                <Flex gap="s" alignItems="center">
                  <AtiraIcon
                    icon={file.isFolder ? faFolder : faFile}
                    color={file.isFolder ? "googleDrive" : "darkerSub"}
                  />
                  <Text>{name}</Text>
                </Flex>
              </Clickable>
            ),
          },
          {
            title: t("common.size"),
            dataIndex: "size",
            key: "size",
            render: (size: number) => (size ? formatBytes(size) : ""),
          },
          {
            title: t("common.modified"),
            dataIndex: "modifiedTime",
            key: "modifiedTime",
            render: (value: string) =>
              value ? new Date(value).toLocaleDateString() : "",
          },
          {
            title: t("common.actions"),
            key: "actions",
            render: (file: GoogleDriveFileDto) => (
              <Flex gap="s">
                {!file.isFolder ? (
                  <Tooltip title={t("common.download")}>
                    <Button
                      icon={faDownload}
                      loading={downloadingId === file.id}
                      onClick={() => onDownload(file)}
                      padding="xs"
                      backgroundColor="transparent"
                      color="main"
                    />
                  </Tooltip>
                ) : null}

                {file.webViewLink ? (
                  <Tooltip title={t("integration.google-drive.openInDrive")}>
                    <Button
                      icon={faArrowUpRightFromSquare}
                      onClick={() =>
                        window.open(file.webViewLink, "_blank", "noopener")
                      }
                      padding="xs"
                      backgroundColor="transparent"
                      color="main"
                    />
                  </Tooltip>
                ) : null}

                <Tooltip title={t("common.rename")}>
                  <Button
                    icon={faEdit}
                    onClick={() => {
                      setRenameTarget(file);
                      setRenameValue(file.name);
                    }}
                    padding="xs"
                    backgroundColor="transparent"
                    color="main"
                  />
                </Tooltip>

                <Tooltip title={t("common.delete")}>
                  <Button
                    icon={faTrash}
                    onClick={() => setDeleteTarget(file)}
                    padding="xs"
                    backgroundColor="transparent"
                    color="danger"
                  />
                </Tooltip>
              </Flex>
            ),
          },
        ]}
      />

      <Modal
        open={createFolderOpen}
        onClose={() => setCreateFolderOpen(false)}
        title={t("integration.google-drive.newFolder")}
        width={"24rem"}
      >
        <Flex flexDirection="column" gap="m">
          <Input
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            placeholder={t("common.name")}
          />
          <Button onClick={onCreateFolder} loading={actionLoading} hover>
            {t("common.create")}
          </Button>
        </Flex>
      </Modal>

      <Modal
        open={Boolean(renameTarget)}
        onClose={() => setRenameTarget(null)}
        title={t("common.rename")}
        width={"24rem"}
      >
        <Flex flexDirection="column" gap="m">
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            placeholder={t("common.name")}
          />
          <Button onClick={onRename} loading={actionLoading} hover>
            {t("common.save")}
          </Button>
        </Flex>
      </Modal>

      <WarningModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={onDelete}
        loading={deleting}
        description={t("integration.google-drive.deleteConfirm", {
          name: deleteTarget?.name,
        })}
      />
    </Flex>
  );
};

export default GoogleDriveRoute;
